import React, { Fragment, useRef, useState, useEffect } from 'react'
import { SendRounded, AttachFile } from '@mui/icons-material'
import { format } from 'date-fns'

import { useTwilioServiceRoomChat } from 'hooks/useTwilioServiceRoomChat'
import Message from 'components/Chat/Message'
import ChatAudioControls, {
  shouldShowChatRecordInSendSlot,
  useChatAudioRecorder,
} from 'components/Chat/ChatAudioControls'
import ChatPendingAttachments from 'components/Chat/ChatPendingAttachments'
import * as S from 'components/Chat/styles'
import { usePendingChatAttachments } from 'hooks/usePendingChatAttachments'
import { useChatAudioRecordEnabled } from 'hooks/useChatAudioRecordEnabled'
import { IComponentProps } from './interfaces'
import {
  getChatAttachmentAcceptAttr,
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_MESSAGE_CHARS,
  validateChatAttachment,
} from 'utils/chatAttachmentLimits'
import {
  chatMessageDayKey,
  formatChatDayDividerLabel,
} from 'utils/chatDayLabel'
import { alertDanger } from 'utils/toast'
import chatAnalytics from 'services/analytics/Chat/chatAnalytics'

function messageAuthorLabel(
  author: string | null | undefined,
  personId: string | undefined,
  professionalId: string | undefined,
  patientName: string | undefined,
  doctorName: string | undefined
): string {
  if (!author) return ''
  if (professionalId && author.includes(professionalId)) {
    return (doctorName ?? '').trim() || 'Médico'
  }
  if (personId && author.includes(personId)) {
    return (patientName ?? '').trim() || 'Paciente'
  }
  if (author.includes('professional:')) {
    return (doctorName ?? '').trim() || 'Médico'
  }
  if (author.includes('patient:')) {
    return (patientName ?? '').trim() || 'Paciente'
  }
  return author
}

/** Rótulo estilo referência: função laranja + (nome) escuro, ex. Doutor(a) (Igor Heije). */
function messageAuthorParts(
  author: string | null | undefined,
  personId: string | undefined,
  professionalId: string | undefined,
  patientName: string | undefined,
  doctorName: string | undefined
): { role: string; name: string } {
  if (!author) {
    return { role: '', name: '' }
  }
  const isDoc =
    (professionalId && author.includes(professionalId)) ||
    author.includes('professional:')
  const isPat =
    (personId && author.includes(personId)) || author.includes('patient:')
  if (isDoc) {
    // Este cliente é o do profissional: mensagem dele é mensagem própria.
    // Papel + nome seriam redundantes — quem lê já sabe quem é — e repetiriam
    // o nome a cada balão.
    return { role: 'Você', name: '' }
  }
  if (isPat) {
    return {
      role: 'Paciente',
      name: (patientName ?? '').trim() || 'Paciente',
    }
  }
  return { role: '', name: author }
}

function isProfessionalIdentity(
  identity: string,
  professionalId: string | undefined
): boolean {
  if (!identity) {
    return false
  }
  if (professionalId && identity.includes(professionalId)) {
    return true
  }
  return identity.includes('professional:')
}

function formatTypingListPTBr(names: string[]): string {
  const clean = names.map((n) => n.trim()).filter(Boolean)
  if (clean.length === 0) {
    return ''
  }
  if (clean.length === 1) {
    return `${clean[0]} está digitando...`
  }
  if (clean.length === 2) {
    return `${clean[0]} e ${clean[1]} estão digitando...`
  }
  const last = clean[clean.length - 1]
  const init = clean.slice(0, -1).join(', ')
  return `${init} e ${last} estão digitando...`
}

const ChatPatient: React.FunctionComponent<IComponentProps> = ({
  scheduleData,
  scheduleUUID,
  doctorName,
  twilioChatFromParent,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const audioRecorder = useChatAudioRecorder({ maxSeconds: 120 })
  const chatAudioRecordEnabled = useChatAudioRecordEnabled()
  const pendingAttachments = usePendingChatAttachments()
  const [text, setText] = useState('')
  const personIdRaw = scheduleData?.person_id
  const personId =
    personIdRaw != null && String(personIdRaw).trim() !== ''
      ? String(personIdRaw).trim()
      : undefined
  const professionalId = scheduleData?.doctor_id
  const patientName = scheduleData?.user_name as string | undefined
  const s = scheduleData as Record<string, string | undefined> | undefined
  const twilioConversationSidFromApi =
    s?.twilio_conversation_sid ?? s?.twilioConversationSid

  const fromParent = Boolean(twilioChatFromParent)
  const localChat = useTwilioServiceRoomChat({
    personId,
    professionalId,
    twilioAs: 'professional',
    twilioConversationSid: twilioConversationSidFromApi,
    appointmentId: scheduleUUID,
    enabled: !fromParent && !!(personId && professionalId),
  })
  const {
    status,
    messages,
    hasMoreMessages,
    loadingMore,
    loadMoreMessages,
    sendText,
    sendMedia,
    sendAttachments,
    sending,
    typingParticipants,
    notifyTyping,
    errorMessage,
    getFreshMediaDownloadUrl,
    deleteMessage,
    deletingMessageSid,
    presenceMessage,
    canReopen,
    reopenConversation,
    reopening,
  } = twilioChatFromParent ?? localChat

  useEffect(() => {
    const t = setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
    return () => clearTimeout(t)
  }, [messages])

  useEffect(() => {
    chatAnalytics.trackScreenViewed({
      appointmentId: scheduleUUID,
      doctorId: scheduleData?.doctor_id,
    })
  }, [scheduleUUID, scheduleData?.doctor_id])

  const handleSend = async () => {
    if (sending || status !== 'ready') return
    const trimmed = text.trim()
    const files = pendingAttachments.files
    if (!trimmed && files.length === 0) return
    setText('')
    pendingAttachments.clear()
    if (files.length > 0) {
      await sendAttachments(files, trimmed || undefined)
      return
    }
    await sendText(trimmed)
  }

  const sendChatAttachment = async (file: File) => {
    const err = validateChatAttachment(file)
    if (err) {
      alertDanger(err)
      return
    }
    if (sending || status !== 'ready') return
    const trimmed = text.trim()
    if (trimmed) {
      setText('')
      await sendText(trimmed, [file])
    } else {
      await sendMedia(file)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files
    if (!selected || selected.length === 0) return
    pendingAttachments.addFiles(selected)
    const el = fileInputRef.current
    if (el) el.value = ''
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSend()
    }
  }

  const handleQuickReply = async (qm: string) => {
    if (sending || status !== 'ready') return
    setText('')
    await sendText(qm)
  }

  const quickMessages = [
    'Estou te aguardando na plataforma.',
    'Consulta encerrada por não comparecimento.',
    'Conexão instável.',
  ]

  const typingOthers = typingParticipants.filter(
    (id) => !isProfessionalIdentity(id, professionalId)
  )
  const typingText =
    typingOthers.length > 0
      ? formatTypingListPTBr(
          typingOthers.map(
            (id) =>
              messageAuthorLabel(
                id,
                personId,
                professionalId,
                patientName,
                doctorName
              ).trim() || id
          )
        )
      : null

  return (
    <S.ChatContainer>
      <h2>Chat</h2>
      <S.ChatContent>
        {status === 'connecting' && <p>Conectando ao chat...</p>}
        {status === 'error' && <p>Erro ao conectar ao chat.</p>}
        {/*
          Textos fixos: a `message` do servidor é volátil por contrato e o texto
          de timeout do SDK cita nome de credencial do Twilio. O detalhe fica no
          log. Em `readOnly` e `unavailable` a escrita já está desabilitada
          pelos `status !== 'ready'` abaixo.
        */}
        {status === 'unavailable' && (
          <p>
            Chat indisponível no momento. Tente novamente em alguns minutos.
          </p>
        )}
        {status === 'readOnly' && (
          <>
            <p>
              {presenceMessage ??
                'Você não está mais participando desta conversa. O histórico continua disponível para leitura.'}
            </p>
            {/*
              Fase 4: o estado deixa de ser terminal. Quando a vaga volta, o
              próprio profissional reabre. Não há retry automático de propósito —
              insistir sozinho contra um serviço sem capacidade é o padrão que
              originou esta migração. Some quando reabrir não resolveria, como
              namespace divergente entre ambientes.
            */}
            {canReopen && (
              <S.ChatLoadPrevious
                type="button"
                onClick={() => void reopenConversation()}
                disabled={reopening}
              >
                {reopening ? 'Reabrindo…' : 'Reabrir conversa'}
              </S.ChatLoadPrevious>
            )}
          </>
        )}
        {/*
          O chat abre só com a página mais recente — histórico antigo é caro e
          quase nunca é o que o profissional precisa ver primeiro. O anterior vem
          por ação explícita, nunca no carregamento.
        */}
        {hasMoreMessages && (
          <S.ChatLoadPrevious
            type="button"
            onClick={() => void loadMoreMessages()}
            disabled={loadingMore}
          >
            {loadingMore ? 'Carregando…' : 'Carregar mensagens anteriores'}
          </S.ChatLoadPrevious>
        )}
        {messages.map((msg, index) => {
          const msgDate = new Date(msg.timestamp)
          const prevDate =
            index > 0 ? new Date(messages[index - 1]!.timestamp) : null
          const showDayDivider =
            !prevDate ||
            chatMessageDayKey(msgDate) !== chatMessageDayKey(prevDate)

          const isDoctor = Boolean(
            professionalId &&
            msg.author &&
            (msg.author.includes(professionalId) ||
              msg.author.includes('professional:'))
          )
          const { role, name: senderName } = messageAuthorParts(
            msg.author,
            personId,
            professionalId,
            patientName,
            doctorName
          )
          return (
            <Fragment key={msg.sid}>
              {showDayDivider ? (
                <S.ChatDayDivider role="separator" aria-label="Dia da conversa">
                  <S.ChatDayDividerLabel>
                    {formatChatDayDividerLabel(msgDate)}
                  </S.ChatDayDividerLabel>
                </S.ChatDayDivider>
              ) : null}
              <Message
                messageSid={msg.sid}
                getFreshMediaDownloadUrl={getFreshMediaDownloadUrl}
                message={msg.body ?? ''}
                time={format(msgDate, 'HH:mm')}
                senderRole={role}
                senderName={senderName}
                nameFallback={messageAuthorLabel(
                  msg.author,
                  personId,
                  professionalId,
                  patientName,
                  doctorName
                )}
                isDoctor={isDoctor}
                fileUrl={msg.attributes?.fileUrl}
                fileName={msg.attributes?.fileName}
                fileSize={msg.attributes?.fileSize}
                contentType={msg.attributes?.contentType}
                canDelete={isDoctor}
                deleting={deletingMessageSid === msg.sid}
                onDelete={() => void deleteMessage(msg.sid)}
              />
            </Fragment>
          )
        })}
        {typingText ? (
          <p style={{ fontStyle: 'italic', fontSize: '1.2rem', color: '#888' }}>
            {typingText}
          </p>
        ) : null}
        <div ref={chatEndRef} />
      </S.ChatContent>

      <S.QuickMessagesContainer>
        {quickMessages.map((qm) => (
          <S.QuickMessageButton
            key={qm}
            type="button"
            disabled={sending || status !== 'ready'}
            onClick={() => void handleQuickReply(qm)}
          >
            {qm}
          </S.QuickMessageButton>
        ))}
      </S.QuickMessagesContainer>

      <S.ChatComposerColumn>
        <ChatPendingAttachments
          files={pendingAttachments.files}
          onRemove={pendingAttachments.removeAt}
          disabled={sending || status !== 'ready'}
        />
        {chatAudioRecordEnabled ? (
          <ChatAudioControls
            part="preview"
            recorder={audioRecorder}
            disabled={sending || status !== 'ready'}
            onSend={sendChatAttachment}
          />
        ) : null}
        <S.ChatBottom>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept={getChatAttachmentAcceptAttr()}
            onChange={handleFileChange}
          />
          <S.AttachButton
            type="button"
            aria-label="Anexar arquivos ou imagens"
            title={`Anexar (até ${MAX_CHAT_ATTACHMENTS})`}
            disabled={
              sending ||
              status !== 'ready' ||
              pendingAttachments.count >= MAX_CHAT_ATTACHMENTS
            }
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachFile />
          </S.AttachButton>
          <S.ChatInput
            placeholder="Digite sua mensagem"
            value={text}
            maxLength={MAX_CHAT_MESSAGE_CHARS}
            onChange={(e) => {
              setText(e.target.value)
              notifyTyping()
            }}
            onKeyDown={handleKeyDown}
          />
          {chatAudioRecordEnabled &&
          shouldShowChatRecordInSendSlot(
            text,
            audioRecorder,
            pendingAttachments.count
          ) ? (
            <ChatAudioControls
              part="button"
              buttonLayout="send"
              recorder={audioRecorder}
              disabled={sending || status !== 'ready'}
              onSend={sendChatAttachment}
            />
          ) : (
            <S.ChatButton
              type="button"
              onClick={() => void handleSend()}
              disabled={
                sending ||
                status !== 'ready' ||
                (text.trim().length < 1 && pendingAttachments.count < 1)
              }
            >
              <SendRounded fontSize="large" />
            </S.ChatButton>
          )}
        </S.ChatBottom>
      </S.ChatComposerColumn>
      <S.ChatInputMeta>
        <S.CharCount $atLimit={text.length >= MAX_CHAT_MESSAGE_CHARS}>
          {text.length}/{MAX_CHAT_MESSAGE_CHARS}
        </S.CharCount>
        {text.length >= MAX_CHAT_MESSAGE_CHARS ? (
          <S.CharLimitWarning>
            Limite máximo de caracteres atingido.
          </S.CharLimitWarning>
        ) : null}
      </S.ChatInputMeta>
      {status === 'ready' && errorMessage ? (
        <S.ChatSendError role="alert">{errorMessage}</S.ChatSendError>
      ) : null}
    </S.ChatContainer>
  )
}

export default ChatPatient
